import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import {
  updateCaseSKU,
  updateEquipmentSKU,
  updatePackageSKU,
  updateSKU,
} from 'api/sku';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { SKU } from 'types/sku';
import { RefType, EditSkuModalProps } from './EditSkuModal.props';

import EditSkuModalView from './EditSkuModal.view';
import { toManufacturerDetailsPayload } from 'components/modules/SkuManufacturerDetails';

const Container = forwardRef<RefType, EditSkuModalProps>((props, ref) => {
  const queryClient = useQueryClient();

  const [sku, setSku] = useState<SKU | null>(null);

  const { type } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_sku) => {
        setSku(_sku);

        onOpen();
      },
    }),
    [onOpen],
  );

  const invalidateSkuQueries = () => {
    queryClient.invalidateQueries(`fetch-all-sku-${type}`);
    queryClient.invalidateQueries({
      queryKey: [`fetch-all-sku-select`],
    });
    queryClient.invalidateQueries(['fetch-skus-by-manufacturer']);
  };

  const { mutate: _updateSku, isLoading } = useMutation(updateSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} updated successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const { mutate: _updateCaseSku, isLoading: isLoadingUpdateSku } = useMutation(
    updateCaseSKU,
    {
      onSuccess: () => {
        successToast(`SKU ${type} updated successfully`);
        invalidateSkuQueries();
        onClose();
      },
    },
  );

  const {
    mutate: _updatePackageSku,
    isLoading: isUpdatingPackageSku,
  } = useMutation(updatePackageSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} updated successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const {
    mutate: _updateEquipmentSku,
    isLoading: isUpdatingEquipmentSku,
  } = useMutation(updateEquipmentSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} updated successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const _props = {
    sku,
    type,
    isOpen,
    onClose,
    isLoading,
    isLoadingUpdateSku,
    isUpdatingPackageSku,
    isUpdatingEquipmentSku,
    onUpdate: async (payload: any) => {
      const formattedPayload = {
        _id: sku?._id,
        ...payload,
        type,
        size: Number(payload?.size || '0'),
        weight: String(payload?.weight || '0'),
        itemsInCase: Number(payload?.itemsInCase || '0'),
        isSuspended: payload?.isSuspended,
        pricePerDelivery: Number(payload?.pricePerDelivery || '0'),
        pricePerReplacement: Number(payload?.pricePerReplacement || '0'),
        ...toManufacturerDetailsPayload(payload, 'update'),
      };

      _updateSku(formattedPayload);
    },
    onUpdateCaseSku: async (payload: any) => {
      const formattedPayload = {
        ...payload,
        type,
        _id: sku?._id,
        isSuspended: payload?.isSuspended,
      };

      _updateCaseSku(formattedPayload);
    },
    onUpdateEquipmentSku: async (payload: any) => {
      _updateEquipmentSku({
        _id: sku?._id,
        name: payload.name,
        category: payload.category,
        // Optional per the ticket: an empty select must not be sent as ''.
        manufacturer: payload.manufacturer || undefined,
      });
    },
    onUpdatePackageSku: async (payload: any) => {
      const formattedPayload = {
        name: payload.name,
        caseSku: payload.caseSku,
        description: payload?.description,
        items:
          payload?.items?.length > 0
            ? payload?.items?.map((i: any) => ({
                ...i,
                quantity: Number(i.quantity),
              }))
            : undefined,
        _id: sku?._id || '',
        type,
        isSuspended: payload?.isSuspended,
        washHubsToBeRemoved: payload?.washHubsToBeRemoved,
        washHubsToBeAdded: payload?.washHubsToBeAdded,
      };

      _updatePackageSku(formattedPayload);
    },
  };

  return <EditSkuModalView {..._props} />;
});

const EditSkuModal = memo(Container);

export default EditSkuModal;
